import React, { useState } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { LoadingSpinner, Button } from '@atoms';
import { SearchInput } from '@molecules';
import { RoleTable, AdminRoleModal, AdminRolePermissionsModal } from '@organisms/admin';
import type { Role } from '@organisms/admin';
import { useAuth } from '@contexts/AuthContext';
import toast from 'react-hot-toast';

export const AdminRolesTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { user } = useAuth();
  const hasAdminRole = user?.roles?.some(role => role.name === 'admin') || false;

  const { 
    data: rolesData, 
    isLoading, 
    error, 
    refetch 
  } = trpc.admin.getAllRoles.useQuery();

  const deleteRoleMutation = trpc.admin.deleteRole.useMutation({
    onSuccess: () => {
      toast.success('Role deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete role');
    },
  });

  const roles = rolesData?.data || [];
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCreateRole = () => {
    setSelectedRole(null);
    setIsEditing(false);
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setIsEditing(true);
    setShowRoleModal(true);
  };

  const handleManagePermissions = (role: Role) => {
    setSelectedRole(role);
    setShowPermissionsModal(true);
  };

  const handleDeleteRole = async (role: Role) => {
    if (window.confirm(`Are you sure you want to delete the role "${role.displayName}"?`)) {
      await deleteRoleMutation.mutateAsync({ id: role.id });
    }
  };

  const handleModalClose = () => {
    setShowRoleModal(false);
    setShowPermissionsModal(false);
    setSelectedRole(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading roles..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-error-500 mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Failed to load roles
        </h3>
        <p className="text-text-secondary mb-4">
          {error.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Role Management</h2>
          <p className="text-text-secondary mt-1">
            Manage user roles and their associated permissions
          </p>
        </div>
        {hasAdminRole && (
          <Button
            onClick={handleCreateRole}
            variant="primary"
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Role</span>
          </Button>
        )}
      </div>

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search roles..."
        className="max-w-md"
      />

      {/* Roles Table */}
      <RoleTable
        roles={roles}
        searchTerm={searchTerm}
        onManagePermissions={hasAdminRole ? handleManagePermissions : undefined}
        onEditRole={hasAdminRole ? handleEditRole : undefined}
        onDeleteRole={hasAdminRole ? handleDeleteRole : undefined}
        isDeleteDisabled={() => deleteRoleMutation.isPending}
        showActions={hasAdminRole}
      />

      {/* Summary */}
      <div className="flex justify-between text-sm text-text-secondary">
        <span>
          Showing {filteredRoles.length} of {roles.length} roles
        </span>
        <span>
          {roles.filter(r => r.isActive).length} active, {roles.filter(r => !r.isActive).length} inactive
        </span>
      </div>

      {/* Modals */}
      {showRoleModal && (
        <AdminRoleModal
          role={selectedRole}
          isEditing={isEditing}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {showPermissionsModal && selectedRole && (
        <AdminRolePermissionsModal
          role={selectedRole}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
